function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}
/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */


import React, { Component } from 'react';
import PropTypes from "prop-types";
import FocusLock from 'react-focus-lock'; // eslint-disable-line import/named

import { EuiOutsideClickDetector } from '../outside_click_detector';

var OutsideEventDetector = function OutsideEventDetector(_ref) {
  var children = _ref.children,
      handleEvent = _ref.handleEvent,
      rest = _objectWithoutProperties(_ref, ["children", "handleEvent"]);

  return React.createElement("div", _extends({
    onMouseDown: handleEvent,
    onTouchStart: handleEvent
  }, rest), children);
};
/**
 * A DOM node, a selector string (which will be passed to
 * `document.querySelector()` to find the DOM node), or a function that
 * returns a DOM node.
 */


OutsideEventDetector.propTypes = {
  handleEvent: PropTypes.any.isRequired
};
export class EuiFocusTrap extends Component {
  state = {
    hasBeenDisabledByClick: false
  };
  lastInterceptedEvent = null;
  preventFocusExit = false;

  componentDidMount() {
    this.setInitialFocus(this.props.initialFocus);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.disabled === true && this.props.disabled === false) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        hasBeenDisabledByClick: false
      });
    }
  } // Programmatically sets focus on a nested DOM node; optional


  setInitialFocus = initialFocus => {
    let node = initialFocus instanceof HTMLElement ? initialFocus : null;

    if (typeof initialFocus === 'string') {
      node = document.querySelector(initialFocus);
    } else if (typeof initialFocus === 'function') {
      node = initialFocus();
    }

    if (!node) return; // `data-autofocus` is part of the 'react-focus-lock' API

    node.setAttribute('data-autofocus', 'true');
  }; // Sets whether the focus trap has been disabled by clicks outside its component tree
  // Only applicable for `clickOutsideDisables`

  toggleDisabled = (shouldDisable = !this.state.hasBeenDisabledByClick) => {
    this.setState({
      hasBeenDisabledByClick: shouldDisable
    });
  }; // Sets whether an event has been prevented from disabling the focus trap
  // Only applicable for `clickOutsideDisables`

  toggleExitPrevented = (shouldPrevent = !this.preventFocusExit) => {
    this.preventFocusExit = shouldPrevent;
  }; // Event handler to determine whether a mouse or key event should disable the focus trap
  // Only applicable for `clickOutsideDisables`

  handleOutsideClick = event => {
    this.toggleExitPrevented(false);
    if (this.preventFocusExit && this.lastInterceptedEvent && event.target === this.lastInterceptedEvent.target) return;
    this.toggleDisabled(true);
  }; // Event handler to capture events from within the focus trap subtree and
  // prevent them from disabling the trap (mostly for portals)
  // Only applicable for `clickOutsideDisables`

  handleBubbledEvent = e => {
    this.lastInterceptedEvent = e.nativeEvent;
    this.toggleExitPrevented(true);
  };

  render() {
    const {
      children,
      clickOutsideDisables = false,
      disabled = false,
      returnFocus = true,
      style,
      ...rest
    } = this.props;
    const isDisabled = disabled || this.state.hasBeenDisabledByClick;
    const lockProps = {
      disabled: isDisabled,
      returnFocus,
      ...rest
    };
    return clickOutsideDisables ? <EuiOutsideClickDetector isDisabled={isDisabled} onOutsideClick={this.handleOutsideClick}>
        <OutsideEventDetector handleEvent={this.handleBubbledEvent}>
          <FocusLock lockProps={{
          style
        }} {...lockProps}>
            {children}
          </FocusLock>
        </OutsideEventDetector>
      </EuiOutsideClickDetector> : <FocusLock lockProps={{
      style
    }} {...lockProps}>
        {children}
      </FocusLock>;
  }

}
EuiFocusTrap.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  clickOutsideDisables: PropTypes.bool,
  initialFocus: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.string.isRequired, PropTypes.func.isRequired]),
  style: PropTypes.any
};

try {
  // @ts-ignore
  EuiFocusTrap.displayName = "EuiFocusTrap"; // @ts-ignore

  EuiFocusTrap.__docgenInfo = {
    "description": "",
    "displayName": "EuiFocusTrap",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "disabled": {
        "defaultValue": null,
        "description": "",
        "name": "disabled",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "returnFocus": {
        "defaultValue": null,
        "description": "will return focus to the previous position on trap disable.",
        "name": "returnFocus",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "allowTextSelection": {
        "defaultValue": null,
        "description": "@deprecated Use persistentFocus=false instead\nenables(or disables) text selection. This also allows not to have ANY focus.",
        "name": "allowTextSelection",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "persistentFocus": {
        "defaultValue": {
          value: "false"
        },
        "description": "enables of disables \"sticky\" behavior, when any focusable element shall be focused.\nThis disallow any text selection on the page.",
        "name": "persistentFocus",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "autoFocus": {
        "defaultValue": {
          value: "true"
        },
        "description": "enables or disables autoFocusing feature.\nIf enabled - will move focus inside Lock, selecting the first or autoFocusable element\nIf disable - will blur any focus on Lock activation.",
        "name": "autoFocus",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "noFocusGuards": {
        "defaultValue": null,
        "description": "disables hidden inputs before and after the lock.",
        "name": "noFocusGuards",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "group": {
        "defaultValue": null,
        "description": "named focus group for focus scattering aka combined lock targets",
        "name": "group",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "onActivation": {
        "defaultValue": null,
        "description": "life-cycle hook, called on lock activation\n@param node the observed node",
        "name": "onActivation",
        "required": false,
        "type": {
          "name": "((node: HTMLElement) => void) | undefined"
        }
      },
      "onDeactivation": {
        "defaultValue": null,
        "description": "life-cycle hook, called on deactivation\n@param node the observed node",
        "name": "onDeactivation",
        "required": false,
        "type": {
          "name": "((node: HTMLElement) => void) | undefined"
        }
      },
      "as": {
        "defaultValue": null,
        "description": "Component to use, defaults to 'div'",
        "name": "as",
        "required": false,
        "type": {
          "name": "\"symbol\" | \"object\" | \"slot\" | \"style\" | \"title\" | \"text\" | \"time\" | \"link\" | \"menu\" | \"dialog\" | \"s\" | \"div\" | \"span\" | \"desc\" | \"header\" | \"filter\" | \"image\" | \"stop\" | \"a\" | \"abbr\" | ... 157 more ... | undefined"
        }
      },
      "lockProps": {
        "defaultValue": null,
        "description": "",
        "name": "lockProps",
        "required": false,
        "type": {
          "name": "{ [key: string]: any; } | undefined"
        }
      },
      "whiteList": {
        "defaultValue": null,
        "description": "Controls focus lock working areas. Lock will silently ignore all the events from `not allowed` areas\n@param activeElement\n@returns true if focus lock should handle activeElement, false if not",
        "name": "whiteList",
        "required": false,
        "type": {
          "name": "((activeElement: HTMLElement) => boolean) | undefined"
        }
      },
      "clickOutsideDisables": {
        "defaultValue": null,
        "description": "",
        "name": "clickOutsideDisables",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "initialFocus": {
        "defaultValue": null,
        "description": "",
        "name": "initialFocus",
        "required": false,
        "type": {
          "name": "string | HTMLElement | (() => HTMLElement) | undefined"
        }
      },
      "style": {
        "defaultValue": null,
        "description": "",
        "name": "style",
        "required": false,
        "type": {
          "name": "CSSProperties | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/focus_trap/focus_trap.tsx#EuiFocusTrap"] = {
      docgenInfo: EuiFocusTrap.__docgenInfo,
      name: "EuiFocusTrap",
      path: "src/components/focus_trap/focus_trap.tsx#EuiFocusTrap"
    };
} catch (__react_docgen_typescript_loader_error) {}